import React, { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { demoFormData } from '../data/pagesData';
import { logoCarousel } from '../data/mockData';
import { CheckCircle, ChevronDown } from 'lucide-react';

/* ───── Animated Counter Component ───── */
const AnimatedCounter = ({ end, suffix, duration = 1800 }) => {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return (
    <span ref={ref} className="text-[#082900] text-[48px] lg:text-[72px] font-light leading-none tracking-[-0.02em]">
      {count}<span className="text-[36px] lg:text-[56px]">{suffix}</span>
    </span>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleOptions = ["Jobseeker", "Employer / Staffing Agency"];
const sourceOptions = ["Referral", "Conference", "Y Combinator", "Internet", "Billboard", "LinkedIn", "Google Search", "Other"];

const DemoPage = () => {
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    role: '',
    company: '',
    jobTitle: '',
    phone: '',
    source: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.includes('@') || !form.email.includes('.')) {
      setError('Please use a valid work email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const nameParts = form.fullName.trim().split(' ');
      const payload = {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: form.email,
        company: form.company,
        role: form.role,
        source: form.source
      };
      const res = await fetch(`${API}/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) { setSubmitted(true); }
      else { setError('Something went wrong. Please try again.'); }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#F9FBF5] min-h-screen">
      <Navbar />

      {/* Hero section with full-bleed background image */}
      <section className="relative min-h-[calc(100vh-80px)] overflow-hidden">
        {/* Background image - full bleed */}
        <div className="absolute inset-0">
          <img
            src={demoFormData.bgImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content overlay */}
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24 min-h-[calc(100vh-80px)] flex items-center">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-end w-full">
            {/* Left - Heading text at bottom */}
            <div className="lg:w-1/2 lg:pb-4">
              <h1 className="text-[#082900] text-[36px] lg:text-[52px] font-light leading-[1.1] tracking-[-0.02em] mb-4">
                Experience Duty AI<br />in a live demo
              </h1>
              <p className="text-[#082900]/70 text-[15px] lg:text-[17px] font-normal">
                Schedule a 30-minute demo with an AI expert.
              </p>
            </div>

            {/* Right - Form card */}
            <div className="lg:w-1/2 w-full">
              {submitted ? (
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-10 lg:p-12 text-center">
                  <CheckCircle className="w-14 h-14 text-[#082900] mx-auto mb-5" strokeWidth={1.5} />
                  <h2 className="text-[#082900] text-[24px] font-light mb-3">Thank you!</h2>
                  <p className="text-[#082900]/60 text-[15px] font-normal">
                    Your demo request has been received. We'll reach out to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 lg:p-10">
                  {/* Work Email */}
                  <div className="mb-7">
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setForm({...form, email: e.target.value})}
                      placeholder="Work Email"
                      className="w-full border-b border-[#082900]/20 pb-3 text-[#082900] text-[15px] font-normal placeholder:text-[#082900]/40 focus:outline-none focus:border-[#082900] transition-colors bg-transparent"
                    />
                  </div>

                  {/* Full name */}
                  <div className="mb-7">
                    <input
                      type="text"
                      required
                      value={form.fullName}
                      onChange={e => setForm({...form, fullName: e.target.value})}
                      placeholder="Full name"
                      className="w-full border-b border-[#082900]/20 pb-3 text-[#082900] text-[15px] font-normal placeholder:text-[#082900]/40 focus:outline-none focus:border-[#082900] transition-colors bg-transparent"
                    />
                  </div>

                  {/* What best describes you? */}
                  <div className="mb-7 relative">
                    <select
                      required
                      value={form.role}
                      onChange={e => setForm({...form, role: e.target.value})}
                      className="w-full border-b border-[#082900]/20 pb-3 text-[15px] font-normal focus:outline-none focus:border-[#082900] transition-colors bg-transparent appearance-none pr-8 cursor-pointer"
                      style={{ color: form.role ? '#082900' : 'rgba(8,41,0,0.4)' }}
                    >
                      <option value="" disabled>What best describes you?</option>
                      {roleOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <ChevronDown className="absolute right-0 top-1 w-4 h-4 text-[#082900]/40 pointer-events-none" />
                  </div>

                  {/* Company name */}
                  <div className="mb-7">
                    <input
                      type="text"
                      value={form.company}
                      onChange={e => setForm({...form, company: e.target.value})}
                      placeholder="Company name"
                      className="w-full border-b border-[#082900]/20 pb-3 text-[#082900] text-[15px] font-normal placeholder:text-[#082900]/40 focus:outline-none focus:border-[#082900] transition-colors bg-transparent"
                    />
                  </div>

                  {/* Job title */}
                  <div className="mb-7">
                    <input
                      type="text"
                      value={form.jobTitle}
                      onChange={e => setForm({...form, jobTitle: e.target.value})}
                      placeholder="Job title"
                      className="w-full border-b border-[#082900]/20 pb-3 text-[#082900] text-[15px] font-normal placeholder:text-[#082900]/40 focus:outline-none focus:border-[#082900] transition-colors bg-transparent"
                    />
                  </div>

                  {/* Phone number */}
                  <div className="mb-7">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm({...form, phone: e.target.value})}
                      placeholder="Phone number"
                      className="w-full border-b border-[#082900]/20 pb-3 text-[#082900] text-[15px] font-normal placeholder:text-[#082900]/40 focus:outline-none focus:border-[#082900] transition-colors bg-transparent"
                    />
                  </div>

                  {/* How did you hear about us? */}
                  <div className="mb-8 relative">
                    <select
                      value={form.source}
                      onChange={e => setForm({...form, source: e.target.value})}
                      className="w-full border-b border-[#082900]/20 pb-3 text-[15px] font-normal focus:outline-none focus:border-[#082900] transition-colors bg-transparent appearance-none pr-8 cursor-pointer"
                      style={{ color: form.source ? '#082900' : 'rgba(8,41,0,0.4)' }}
                    >
                      <option value="" disabled>How did you hear about us?</option>
                      {sourceOptions.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <ChevronDown className="absolute right-0 top-1 w-4 h-4 text-[#082900]/40 pointer-events-none" />
                  </div>

                  {error && <p className="text-red-600 text-[13px] mb-4">{error}</p>}

                  <p className="text-[#082900]/40 text-[13px] font-normal mb-5">
                    By clicking "Book Demo", you agree to Duty AI's{' '}
                    <a href="/privacy-policy" className="underline hover:text-[#082900]/60">Privacy Policy</a>
                  </p>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-10 py-3.5 bg-[#082900] text-[#EEF2E1] text-[15px] font-normal rounded-full hover:bg-[#0a3500] transition-colors duration-300 disabled:opacity-50"
                  >
                    {loading ? 'Submitting...' : 'Book a Demo'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Logo bar */}
      <div className="py-16 bg-[#F9FBF5]">
        <p className="text-center text-[#082900]/60 text-[13px] font-normal tracking-wide mb-8">
          Our AI recruiter is driving results across industries globally
        </p>
        <div className="relative overflow-hidden">
          <div className="logo-carousel-track flex items-center gap-16">
            {[...logoCarousel.logos, ...logoCarousel.logos, ...logoCarousel.logos].map((l, i) => (
              <img key={i} src={l.url} alt={l.name} className="h-8 md:h-10 w-auto object-contain flex-shrink-0 opacity-80" loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* Measurable Impact */}
      <section className="bg-[#F9FBF5] py-16 lg:py-24">
        <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
          <h2 className="text-[#082900]/30 text-[36px] lg:text-[64px] font-light leading-[1] tracking-[-0.02em] mb-12 lg:mb-16">
            Measurable Impact
          </h2>
          <div className="space-y-8 lg:space-y-10">
            {[
              { label: 'Candidates interviewed', end: 1, suffix: 'Million+' },
              { label: 'Candidates who prefer Duty AI', end: 96, suffix: '%' },
              { label: 'Faster time-to-hire', end: 2, suffix: 'x' },
              { label: 'Officially supported ATS integrations', end: 33, suffix: '+' },
              { label: 'Languages supported', end: 26, suffix: '+' },
            ].map((stat, i) => (
              <div key={i} className="border-t border-[#082900]/10 pt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                <span className="text-[#082900]/60 text-[16px] lg:text-[18px] font-normal">{stat.label}</span>
                <AnimatedCounter end={stat.end} suffix={stat.suffix} duration={1800 + i * 200} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DemoPage;
